import React from 'react';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { noop } from 'lodash';
import { developerProjectsCountersSelector } from 'screens/DeveloperPage/selectors';
import { DeveloperProjectSection } from 'utils/entities';
import { useScreenBreakpoint } from 'consts/breakpoints';
import PlanningIcon from 'assets/svg/developer/planned.svg';
import BuildingIcon from 'assets/svg/developer/in-progress.svg';
import DoneIcon from 'assets/svg/developer/populated.svg';
import { SectionTitle, SubsectionWrapper } from 'screens/DeveloperPage/styled';
import { useLocale } from 'locale';
import * as Styled from './styled';
import scrollIntoView from 'scroll-into-view';
import { AUTO_SCROLL_DURATION } from 'components/navigation/subheader/SectionsNavigation';
import {
  DESKTOP_HEADER_TOTAL_HEIGHT,
  HEADER_HEIGHT,
  MOBILE_HEADER_TOTAL_HEIGHT,
} from 'components/navigation/header.constants';
import { genProjectSectionScrollSpy } from 'screens/DeveloperPage/helpers';

interface ProjectCountersProps {
  countersBySection: Record<DeveloperProjectSection, number>;
}

const statusToIcon = {
  [DeveloperProjectSection.Planning]: <PlanningIcon />,
  [DeveloperProjectSection.Building]: <BuildingIcon />,
  [DeveloperProjectSection.Done]: <DoneIcon />,
};

const ProjectCounters: React.FC<ProjectCountersProps> = (props) => {
  const isMobile = useScreenBreakpoint() === 1;
  const { t } = useLocale();

  const { planning, building, done } = props.countersBySection;

  const topOffset = isMobile
    ? MOBILE_HEADER_TOTAL_HEIGHT
    : DESKTOP_HEADER_TOTAL_HEIGHT + HEADER_HEIGHT;

  const onCounterClick = (section: DeveloperProjectSection) => {
    const scrollSettings = {
      time: AUTO_SCROLL_DURATION,
      align: {
        topOffset,
        top: 0,
      },
    };
    const projectsSection = document.querySelector<HTMLElement>(`[data-scroll-spy-id="${genProjectSectionScrollSpy(section)}"]`);

    scrollIntoView(projectsSection, scrollSettings);
  };

  const items = [ {
    status: DeveloperProjectSection.Planning,
    value: planning,
    onClick: planning > 0 ? () => onCounterClick(DeveloperProjectSection.Planning) : noop,
  }, {
    status: DeveloperProjectSection.Building,
    value: building,
    onClick: building > 0 ? () => onCounterClick(DeveloperProjectSection.Building) : noop,
  }, {
    status: DeveloperProjectSection.Done,
    value: done,
    onClick: done > 0 ? () => onCounterClick(DeveloperProjectSection.Done) : noop,
  } ];


  return (
    <SubsectionWrapper withBorder={isMobile} noTopMargin={!isMobile}>
      <SectionTitle>{t('developerPage.projectCounters.title')}</SectionTitle>
      <Styled.ProjectCountersWrapper>
        {items.map(item => (
          <Styled.ProjectCounterItem
            key={item.status}
            onClick={item.onClick}
            isClickable={item.value > 0}
          >
            <Styled.ProjectCounterIcon>
              {statusToIcon[item.status]}
            </Styled.ProjectCounterIcon>
            <Styled.ProjectCounterItemInner>
              <Styled.ProjectCounterNumber weight="bold">
                {item.value}
              </Styled.ProjectCounterNumber>
              <Styled.ProjectCounterDescription weight="medium">
                {t('developerPage.projectCounters.counterDescription', { isMobile, status: item.status })}
              </Styled.ProjectCounterDescription>
            </Styled.ProjectCounterItemInner>
          </Styled.ProjectCounterItem>
        ))}
      </Styled.ProjectCountersWrapper>
    </SubsectionWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  countersBySection: developerProjectsCountersSelector(state),
});

export default connect(mapStateToProps)(ProjectCounters);
